package com.evoapps.evoshift.domain.usecase

import com.evoapps.evoshift.domain.model.DayTemplate
import com.evoapps.evoshift.domain.repository.WorkplaceRepository

// domain/usecase/skeleton/AddShiftToDayTemplateUseCase.kt
class AddShiftToDayTemplateUseCase(
    private val workplaceRepository: WorkplaceRepository
) {
    suspend operator fun invoke(
        workplaceId: String,
        dayTemplateId: String,
        shiftTemplateId: String
    ): Result<Unit> {
        return try {
            // 1. Fetch
            val templates = workplaceRepository.getDayTemplates(workplaceId).getOrThrow()
            val targetTemplate = templates.find { it.id == dayTemplateId }
                ?: throw IllegalStateException("DayTemplate not found")

            // 2. Modify (Business Logic: Append ID)
            val newShiftIds = targetTemplate.shiftTemplateIds + shiftTemplateId
            val updatedTemplate = targetTemplate.copy(shiftTemplateIds = newShiftIds)

            // 3. Save
            workplaceRepository.updateDayTemplate(workplaceId, updatedTemplate)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }
}

// domain/usecase/skeleton/RemoveShiftFromDayTemplateUseCase.kt
class RemoveShiftFromDayTemplateUseCase(
    private val workplaceRepository: WorkplaceRepository
) {
    suspend operator fun invoke(
        workplaceId: String,
        dayTemplateId: String,
        indexToRemove: Int
    ): Result<Unit> {
        return try {
            val templates = workplaceRepository.getDayTemplates(workplaceId).getOrThrow()
            val targetTemplate = templates.find { it.id == dayTemplateId }
                ?: throw IllegalStateException("DayTemplate not found")

            // Business Logic: Safe removal by index
            val currentIds = targetTemplate.shiftTemplateIds.toMutableList()
            if (indexToRemove in currentIds.indices) {
                currentIds.removeAt(indexToRemove)
                val updatedTemplate = targetTemplate.copy(shiftTemplateIds = currentIds)
                workplaceRepository.updateDayTemplate(workplaceId, updatedTemplate)
            } else {
                Result.success(Unit) // No-op if index invalid
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }
}

// domain/usecase/skeleton/CreateDayTemplateUseCase.kt
class CreateDayTemplateUseCase(
    private val workplaceRepository: WorkplaceRepository
) {
    suspend operator fun invoke(workplaceId: String, name: String): Result<String> {
        val newTemplate = DayTemplate(id = "", name = name, shiftTemplateIds = emptyList())
        return workplaceRepository.createDayTemplate(workplaceId, newTemplate)
    }
}